import { domain } from '../d-man';
import { components } from './api/specs';
import { ObjectList } from './ObjectList';

type schemas = components['schemas'];

export type UserRoleDTOWithName = schemas['UserRoleDTOWithoutName'];

export type UserRole = {
    userName: string;
    projectId?: string;
    businessUnitId?: string;
    creditorId?: string;
    roleName: string;
};

export type Role = {
    id: string;
    name: string;
    scope: string;
    displayOrder: number;
};

export function useGetRoles() {
    return domain.useGet<Role[]>('/roles');
}

export function useCreateUserRole() {
    return domain.usePost<UserRole, null>('/users/:userName/role', {
        transformRequest: (request: UserRole) => ({
            urlParams: { userName: request.userName },
            data: request,
        }),
    });
}

export function useGetUserRoles() {
    return domain.useGet<UserRole[]>('/userroles');
}

export function useGetUserRolesByRoleDTO(userRole: UserRoleDTOWithName) {
    return domain.useGet<UserRole[]>('/userroles', {
        requestConfig: {
            queryParams: userRole,
        },
    });
}

export function useDeleteUserRoles() {
    return domain.useDelete<null, UserRoleDTOWithName & { userName: string }>(
        '/users/:userName/role',
        {
            transformRequest: (request: UserRoleDTOWithName & { userName: string }) => ({
                urlParams: { userName: request.userName },
                data: request,
            }),
        }
    );
}

/**
 * Roles with the information needed to choose one: what an assignment must be
 * scoped to, and what the role actually allows.
 *
 * Roles carry no description in the database, so "level4" means nothing on its
 * own - this is what makes the role pickers legible.
 */
export type RoleDetail = {
    id: string;
    name: string;
    displayOrder: number;
    scope: number;
    /** Global, Project, Business Unit, Creditor or Company. */
    scopeType: string;
    isGlobal: boolean;
    permissionCount: number;
    permissions: { code: string; description: string; category: string }[];
};

export function useGetRoleDetail() {
    return domain.useGet<ObjectList<RoleDetail>>('/roles/detail');
}
